/**
 * OperationsTab.js — Phase 2: fetches from GET /api/hr/attendance/summary
 */
import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, Typography, Box, Table, TableHead, TableBody, TableRow, TableCell, Chip, CircularProgress, Alert, Tooltip as MuiTooltip } from '@mui/material';
import { BarChart, ComposedChart, Bar, Line, ReferenceLine, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { EventAvailable, TrafficRounded, DateRange, ExitToApp } from '@mui/icons-material';
import MetricCard from '../components/MetricCard';
import LeaveBurnoutPanel from '../components/LeaveBurnoutPanel';
import { attendanceApi } from '../services/hrmsApi';
import { useChatContext } from '../context/ChatContext';

// ── Shared pie-label constants (fixes b, c, d) ────────────────────────────────
const RADIAN           = Math.PI / 180;

const getAttendanceColor = (value) => {
  if (value >= 90) return '#4CAF50';
  if (value >= 75) return '#FFC107';
  return '#EF5350';
};
const LEGEND_FONT_SIZE = 12;
const LABEL_FONT_SIZE  = 12;

// Renders "XX%" in the segment's own colour; skips slices < 5% (fix b)
const renderColoredPercentLabel = ({ cx, cy, midAngle, outerRadius, value, fill, percent }) => {
  if (percent < 0.01) return null;
  const radius = outerRadius + 10;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill={fill} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={LABEL_FONT_SIZE} fontWeight={600}>
      {`${value}%`}
    </text>
  );
};

function TrafficLight({ status }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 2 }}>
      {['green','yellow','red'].map((c) => (
        <Box key={c} sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: c === status ? (c === 'green' ? '#4CAF50' : c === 'yellow' ? '#FFC107' : '#EF5350') : '#E0E0E0', boxShadow: c === status ? `0 0 24px ${c === 'green' ? '#4CAF50' : c === 'yellow' ? '#FFC107' : '#EF5350'}80` : 'none' }} />
      ))}
      {status === 'off' && (
        <Typography variant="caption" color="text.disabled" mt={0.5} textAlign="center">Non-working day</Typography>
      )}
    </Box>
  );
}

function heatColor(val) {
  if (val >= 16) return '#C62828'; if (val >= 12) return '#EF5350';
  if (val >= 8)  return '#FF8A65'; if (val >= 4)  return '#FFCC80';
  return '#E8F5E9';
}

export default function OperationsTab() {
  const { openWithMessage } = useChatContext();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    attendanceApi.getSummary()
      .then(setData)
      .catch((e) => setError(e.response?.data?.message || 'Failed to load attendance data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;

  const { presentPct, presentCount, totalCount, latenessIncidents, leaveLiability, earlyLeaverTotal, leaveData, latenessHeatmapData, earlyLeavers, attendanceWeekData, isWorkingDay = true } = data;
  const trafficLight = isWorkingDay === false ? 'off'
    : presentPct >= 90 ? 'green'
    : presentPct >= 75 ? 'yellow'
    : 'red';

  // ── Dynamic MetricCard insight / status ──────────────────────────────────────
  const attendanceStatus  = isWorkingDay === false ? 'blue'
    : presentPct >= 90 ? 'green'
    : presentPct >= 75 ? 'yellow'
    : 'red';
  const attendanceInsight = isWorkingDay === false ? 'ℹ️ No attendance data recorded today'
    : presentPct >= 90 ? '🟢 Operations Stable — within threshold'
    : presentPct >= 75 ? `🟡 Attendance below target — ${presentPct}% present`
    : `🔴 Attendance critical — only ${presentPct}% present`;

  const latenessStatus  = latenessIncidents == null ? 'blue'
    : latenessIncidents <= 20 ? 'green'
    : latenessIncidents <= 50 ? 'yellow'
    : 'red';
  const latenessInsight = latenessIncidents == null ? '🔵 Data unavailable'
    : latenessIncidents <= 20 ? `🟢 Low lateness — ${latenessIncidents} incidents this month`
    : latenessIncidents <= 50 ? `🟡 Moderate — ${latenessIncidents} incidents, monitor trend`
    : `🔴 High lateness — ${latenessIncidents} incidents, action required`;

  const leaveLiabilityStatus  = leaveLiability == null ? 'blue'
    : leaveLiability < 200000 ? 'green'
    : leaveLiability < 400000 ? 'yellow'
    : 'red';
  const leaveLiabilityInsight = leaveLiability == null ? '🔵 Data unavailable'
    : leaveLiability < 200000 ? '🟢 Leave liability within healthy range'
    : leaveLiability < 400000 ? '🟡 Alert: High leave liability — action required'
    : `🔴 Critical liability — RM ${leaveLiability?.toLocaleString()} at risk`;

  const earlyLeaversStatus  = earlyLeaverTotal == null ? 'blue'
    : earlyLeaverTotal <= 20 ? 'green'
    : earlyLeaverTotal <= 60 ? 'yellow'
    : 'red';
  const earlyLeaversInsight = earlyLeaverTotal == null ? '🔵 Data unavailable'
    : earlyLeaverTotal <= 20 ? '🟢 Low early-leaver incidents this month'
    : earlyLeaverTotal <= 60 ? `🟡 ${earlyLeaverTotal} incidents — review shift scheduling`
    : `🔴 High early-leaver rate — ${earlyLeaverTotal} incidents`;

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard
          title="Today's Attendance"
          value={isWorkingDay === false ? '—' : `${presentPct}%`}
          subtitle={isWorkingDay === false ? 'Non-working day' : `${presentCount?.toLocaleString()} / ${totalCount?.toLocaleString()} present`}
          icon={EventAvailable}
          iconColor={isWorkingDay === false ? '#9E9E9E' : '#2E7D32'}
          insight={attendanceInsight}
          status={attendanceStatus}
          onAskAI={isWorkingDay === false ? null : () => openWithMessage(`Today's attendance rate is ${presentPct}% (${presentCount} of ${totalCount} present). What patterns should HR watch and are there departments consistently below target?`)}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard title="Lateness Incidents" value={latenessIncidents} subtitle="This month" icon={DateRange} iconColor="#C62828" insight={latenessInsight} status={latenessStatus}
          onAskAI={() => openWithMessage(`There have been ${latenessIncidents} lateness incidents this month, with a pattern of Monday lateness in Finance. What are the root causes and how should management respond?`)} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard title="Leave Liability" value={`RM ${leaveLiability?.toLocaleString()}`} subtitle="Unused AL (This month)" icon={ExitToApp} iconColor="#F57F17" insight={leaveLiabilityInsight} status={leaveLiabilityStatus}
          onAskAI={() => openWithMessage(`Unused annual leave liability is RM ${leaveLiability?.toLocaleString()}. What strategies can reduce this liability before year-end and which departments are most at risk?`)} />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <MetricCard title="Early Leavers" value={earlyLeaverTotal} subtitle="Total incidents this month" icon={TrafficRounded} iconColor="#0277BD" insight={earlyLeaversInsight} status={earlyLeaversStatus}
          onAskAI={() => openWithMessage(`There were ${earlyLeaverTotal} early-leaver incidents this month, with Unit 4's evening shift consistently affected. What could be causing this and what interventions should HR consider?`)} />
      </Grid>

      <Grid item xs={12} sm={5} md={2}>
        <Card sx={{ height: 310 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Today's Status</Typography>
            <TrafficLight status={trafficLight} />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={7} md={5}>
        <Card sx={{ height: 310 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Weekly Attendance (Headcount & Percentage %)</Typography>
            {/*<Typography variant="caption" color="text.secondary">Headcount & Percentage %</Typography>*/}
            {/* BACKUP — pre-combo-chart version, percentage-only bars
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={attendanceWeekData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} />
                <Tooltip formatter={(v) => [`${v}%`]} />
                <Bar dataKey="present" name="Present %" radius={[4,4,0,0]}>
                  {(attendanceWeekData || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getAttendanceColor(entry.present)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            */}
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={attendanceWeekData} margin={{ top: 5, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v, name) => [name === 'Present %' ? `${v}%` : v, name]} />
                <Bar yAxisId="left" dataKey="presentCount" name="Present (headcount)" radius={[4,4,0,0]}>
                  {(attendanceWeekData || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getAttendanceColor(entry.present)} />
                  ))}
                </Bar>
                <Line yAxisId="right" type="monotone" dataKey="present" name="Present %" stroke="#1565C0" strokeWidth={2} dot={{ r: 3 }} />
                {attendanceWeekData?.length > 0 && (
                  <ReferenceLine
                    yAxisId="right"
                    y={attendanceWeekData[attendanceWeekData.length - 1].present}
                    stroke="#1565C0"
                    strokeDasharray="4 4"
                    strokeOpacity={0.5}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={5}>
        <Card sx={{ height: 310 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Leave Usage Breakdown (Monthly)</Typography>
            {/*<ResponsiveContainer width="100%" height={235}>*/}
            <ResponsiveContainer width="100%" height={255}>
              <PieChart>
                <Pie
                  data={leaveData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  labelLine={false}
                  label={renderColoredPercentLabel}
                >
                  {(leaveData || []).map((e) => <Cell key={e.name} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={(v) => [`${v}%`]} />
                <Legend
                  verticalAlign="bottom"
                  formatter={(value) => (
                    <span style={{ fontSize: LEGEND_FONT_SIZE, color: '#555' }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ height: 330 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Lateness Heatmap</Typography>
            {/*<Typography variant="caption" color="text.secondary" display="block" mb={1.5}>🔴 Consistent Monday lateness pattern detected by AI</Typography>*/}
            <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 255 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>Week</TableCell>
                    {['Mon','Tue','Wed','Thu','Fri'].map((d) => <TableCell key={d} align="center" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>{d}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(latenessHeatmapData || []).map((row) => (
                    <TableRow key={row.week}>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>{row.week}</TableCell>
                      {['Mon','Tue','Wed','Thu','Fri'].map((d) => {
                        const cell  = row[d] || {};
                        const count = cell.count || 0;
                        const dept  = cell.dept  || '';
                        return (
                          <TableCell key={d} align="center" sx={{ p: '4px' }}>
                            <MuiTooltip title={dept ? `${dept}: ${count}` : ''} arrow placement="top">
                              <Box sx={{ width: 44, height: 36, borderRadius: 1, bgcolor: heatColor(count), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', fontWeight: 700, fontSize: '0.8rem', color: count >= 12 ? '#fff' : '#333' }}>
                                {count}
                              </Box>
                            </MuiTooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ height: 330 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Top 5 Units — Early Leavers</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['#','Unit','Dept','Incidents','Avg. Mins'].map((h) => <TableCell key={h} sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.73rem' }}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {(earlyLeavers || []).map((row) => (
                  <TableRow key={row.rank} sx={{ '&:hover': { bgcolor: '#F8FAFF' } }}>
                    <TableCell><Chip label={row.rank} size="small" sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 700, width: 28, height: 24 }} /></TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{row.unit}</TableCell>
                    <TableCell><Chip label={row.dept} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /></TableCell>
                    <TableCell sx={{ fontWeight: 700, color: row.incidents >= 20 ? 'error.main' : 'text.primary' }}>{row.incidents}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>{row.avgMins}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <LeaveBurnoutPanel />
      </Grid>
    </Grid>
  );
}
